import axiosInstance from "../config/axios";
import axios from "axios";
import API_ENDPOINTS from "../config/endpoints";

// Helper: build full profile image URL from server value
const IMAGE_BASE = import.meta.env.VITE_API_IMAGE_PATH;
const buildProfileImageUrl = (value) => {
  if (!value) return null;
  const str = String(value);
  if (/^https?:\/\//.test(str)) return str; // already absolute URL
  const trimmed = str.replace(/^\/+/, "");
  // If server already includes 'profile/...' keep it without duplicating
  return trimmed.startsWith("profile/")
    ? `${IMAGE_BASE}/${trimmed}`
    : `${IMAGE_BASE}/profile/${trimmed}`;
};

// Auth Service - Login
export const loginUser = async (email, password, rememberMe = false) => {
  try {
    const payload = { email, password, rememberMe };
    const data = await axiosInstance.post(API_ENDPOINTS.LOGIN, payload);

    // Expecting response like: { accessToken: "...", user?: {...} }
    const token = data?.accessToken || data?.token || null;
    const user = data?.user || null;

    return { token, user };
  } catch (error) {
    throw error; // Centralized handler formats the error
  }
};

export const registerUser = async (userData) => {
  try {
    const response = await axiosInstance.post(API_ENDPOINTS.REGISTER, userData);
    return response;
  } catch (error) {
    throw error;
  }
};

export const logoutUser = async () => {
  try {
    // const response = await axiosInstance.get(API_ENDPOINTS.LOGOUT);
    // return response;
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve({ success: true });
      }, 300);
    });
  } catch (error) {
    throw error;
  }
};

export const getUserData = async () => {
  try {
    const response = await axiosInstance.get(API_ENDPOINTS.GET_USER);
    
    // Handle both response.data and direct response structures
    // Axios interceptor returns response.data, but API might nest data again
    const apiData = response?.data || response;
    
    // Map the API response to match our expected format
    const userData = {
      id: apiData.id,
      name: apiData.name,
      email: apiData.email,
      phone: apiData.mobile,
      dob: apiData.dob,
      gender: apiData.gender,
      passport: apiData.passport_number || "",
      pan: apiData.pan || "",
      profilePicture: buildProfileImageUrl(apiData.profile_picture) || `https://ui-avatars.com/api/?name=${encodeURIComponent(apiData.name || 'User')}&background=B3B3B3&color=fff&size=46`,
      totalDonerAmounts: apiData.totalDonerAmounts ?? apiData.totalDonorAmounts ?? 0,
      totalcampaign: apiData.totalcampaign ?? apiData.totalCampaign ?? 0,
      // Role information (for checking admin access)
      Role: apiData.Role || apiData.role || null,
    };
    
    return userData;
  } catch (error) {
    throw error;
  }
};

export const updateUserData = async (userData) => {
  try {
    // Create FormData for multipart/form-data to handle file upload properly
    const formData = new FormData();
    
    // Add all user fields to FormData
    formData.append('id', userData.id);
    formData.append('name', userData.name);
    formData.append('email', userData.email);
    formData.append('mobile', userData.phone); // API expects 'mobile'
    formData.append('dob', userData.dob);
    formData.append('gender', userData.gender);
    // Fix: backend expects 'passport_number' instead of 'passport'
    formData.append('passport_number', userData.passport || "");
    formData.append('pan', userData.pan || "");
    
    // Handle profile picture - if it's a base64 string from FileReader
    if (userData.profilePicture && userData.profilePicture.startsWith('data:image')) {
      // Convert base64 to blob
      const response = await fetch(userData.profilePicture);
      const blob = await response.blob();
      formData.append('profile_picture', blob, 'profile.jpg');
    } else if (userData.profilePicture) {
      // If it's already a URL or filename, pass it as is
      formData.append('profile_picture', userData.profilePicture);
    }

    // Set content type to undefined to let axios set the correct multipart boundary
    const response = await axiosInstance.put(API_ENDPOINTS.UPDATE_USER, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
 
    // Handle both response.data and direct response structures
    const apiData = response?.data || response;
 
    // Map API response back to our standard format
    const updated = {
      id: apiData.id,
      name: apiData.name,
      email: apiData.email,
      phone: apiData.mobile,
      dob: apiData.dob,
      gender: apiData.gender,
      passport: apiData.passport_number || "",
      pan: apiData.pan || "",
      profilePicture: buildProfileImageUrl(apiData.profile_picture) || `https://ui-avatars.com/api/?name=${encodeURIComponent(apiData.name || 'User')}&background=B3B3B3&color=fff&size=46`,
      totalDonerAmounts: apiData.totalDonerAmounts ?? apiData.totalDonorAmounts ?? 0,
      totalcampaign: apiData.totalcampaign ?? apiData.totalCampaign ?? 0,
    };
 
    return updated;
  } catch (error) {
    throw error;
  }
};

// Forgot Password - request code to be sent to email
export const requestPasswordReset = async (email) => {
  try {
    const response = await axiosInstance.post(API_ENDPOINTS.FORGOT_PASSWORD, { email });
    return response; // expecting { success: true, message }
  } catch (error) {
    throw error;
  }
};

// Verify Code - verify email + code
export const verifyResetCode = async (email, code) => {
  try {
    const response = await axiosInstance.post(API_ENDPOINTS.VERIFY_CODE, { email, code });
    return response; // e.g., { success: true }
  } catch (error) {
    throw error;
  }
};

// Change Password - finalize reset
export const changePassword = async (email, code, newPassword, confirmPassword, token) => {
  try {
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    const response = await axiosInstance.post(
      API_ENDPOINTS.RESET_PASSWORD,
      { email, code, new_password: newPassword, confirm_password: confirmPassword },
      { headers }
    );
    return response; // { success: true }
  } catch (error) {
    throw error;
  }
};

// Send Account Email - send registration email with password
export const sendAccountEmail = async (name, email, mobile, password) => {
  try {
    const baseURL = import.meta.env.VITE_API_BASE_URL;
    // Match Dog Home pattern: baseURL/api/send-account-email
    const response = await axios.post(`${baseURL}${API_ENDPOINTS.SEND_EMAIL}`, {
      fullName: name,
      email,
      phoneNumber: mobile,
      password: password
    });
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

// Auto Login with Email - login without password using email only
export const autoLoginWithEmail = async (email) => {
  try {
    const response = await axiosInstance.post(API_ENDPOINTS.AUTO_LOGIN, { email });
    return response; // { accessToken, user }
  } catch (error) {
    throw error;
  }
};

// Send OTP to Email
export const sendOTP = async (email) => {
  try {
    const response = await axiosInstance.post(API_ENDPOINTS.SEND_OTP, { email });
    return response; // { success: true, message: "...", email: "..." }
  } catch (error) {
    throw error;
  }
};

// Verify OTP
export const verifyOTP = async (email, code) => {
  try {
    const response = await axiosInstance.post(API_ENDPOINTS.VERIFY_OTP, { email, code });
    return response; // { success: true, message: "...", email: "..." }
  } catch (error) {
    throw error;
  }
};